import { createContext, useContext, ReactNode, useMemo, useCallback } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import { sacrificeApi } from '../config/sacrifice-api'
import type {
  SacrificeStatsResponse,
  NetworkSacrificeStats,
  TokenSacrificeStats,
  SupportedTokenMetadataAndSearchable,
  UserStatsData,
  ExposedPricedSacrificeData,
} from 'sacrifice-utils/types'
import { useAuth, useBelowFold, useProjects } from '.'
import * as keys from '@provex/eip712-utils/keys'
import { waitForImageLoading } from '../utils/delayed-image-loading'
import { aggregateNetworkStats } from './sacrifice-stats-aggregation'

interface SacrificeStatsContextValue {
  data: SacrificeStatsResponse | undefined
  isLoading: boolean
  isFetching: boolean
  isPlaceholderData: boolean
  error: Error | null
  lastUpdated: Date | null
  refetch: () => Promise<void>
  networks: NetworkSacrificeStats[]
  totalSacrifices: bigint
  totalPoints: bigint
  pointsVersion: string | null
  sacrificeTotalValue: bigint
  tokensByNetwork: Map<string, TokenSacrificeStats[]>
  supportedTokens: SupportedTokenMetadataAndSearchable[]
  getTokensByNetworkId: (networkId: keys.SerializedNetworkIdentifier) => TokenSacrificeStats[]
  getSupportedTokensForNetwork: (
    networkId: keys.SerializedNetworkIdentifier
  ) => SupportedTokenMetadataAndSearchable[]
  networkFromId: (id: keys.SerializedNetworkIdentifier) => NetworkSacrificeStats | null
  userSacrifices: ExposedPricedSacrificeData[] | null
  userStats: UserStatsData | null
  userDestinationAddress: string | null
  userReferrerAddress: string | null
  targetAddress: string | null
  isQueryEnabled: boolean
}

const SacrificeStatsContext = createContext<SacrificeStatsContextValue | undefined>(undefined)

export const useSacrificeStats = (): SacrificeStatsContextValue => {
  const context = useContext(SacrificeStatsContext)
  if (context === undefined) {
    throw new Error('useSacrificeStats must be used within a SacrificeStatsProvider')
  }
  return context
}

interface SacrificeStatsProviderProps {
  children: ReactNode
}

export const SacrificeStatsProvider = ({ children }: SacrificeStatsProviderProps) => {
  const { projectId, userAddresses } = useProjects()
  const { address, authData, recoveredAddress } = useAuth()
  const { isBelowFoldVisible } = useBelowFold()
  const location = useLocation()

  // Only send auth data when viewing our own address, not when viewing another user's address
  const isViewingOwnAddress = useMemo(() => {
    if (!recoveredAddress || !address) return false
    return recoveredAddress.toLowerCase() === address.toLowerCase()
  }, [recoveredAddress, address])

  // Check if we're on a user page - user pages should load data immediately
  // User pages are always visible (not wrapped in LazyLoadOnScroll), so they need data right away
  const isUserPage = useMemo(() => {
    return location.pathname.includes('/user')
  }, [location.pathname])

  // Fetch stats - defer until below-the-fold content is visible (for global page)
  // OR enable immediately if on user page (user page content is above the fold)
  // The graph can render without full data, so we don't need stats immediately on global page
  // This reduces critical path latency by deferring the /stats/sacrifices API call on global page
  // When address changes (from route params), the query will automatically refetch with the new address
  const queryEnabled = !!projectId && (isUserPage || isBelowFoldVisible)

  const { data, isPending, isFetching, isPlaceholderData, error, dataUpdatedAt, refetch } =
    useQuery({
      queryKey: [
        'sacrificeStats',
        projectId ?? '',
        (address ?? '').toLowerCase(),
        // Only include auth in cache key when viewing own address
        isViewingOwnAddress ? (authData?.signature ?? '') : '',
      ],
      queryFn: async ({ signal }) => {
        // Defer until after critical path completes (2s delay, page load, or user interaction)
        await waitForImageLoading()
        return await sacrificeApi.stats.publicSacrificeData({
          request: {
            projectId: projectId!,
            address: address ?? undefined,
            // Only include auth if viewing our own address to get private data (roles, referrals)
            ...(isViewingOwnAddress &&
              authData?.signature &&
              authData?.inputs && {
                signature: authData.signature,
                inputs: authData.inputs,
              }),
          },
          signal,
        })
      },
      // Only fetch when projectId is available AND below-the-fold content is visible
      // This defers the API call until the user scrolls to content that needs the full stats
      enabled: queryEnabled,
      staleTime: 2 * 60 * 1000, // 2 minutes
      gcTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: false, // Don't refetch on mount - only fetch when explicitly enabled
      refetchOnReconnect: false, // Don't refetch on reconnect - only fetch when explicitly enabled
      placeholderData: keepPreviousData,
    })

  const lastUpdated = useMemo(
    () => (dataUpdatedAt ? new Date(dataUpdatedAt) : null),
    [dataUpdatedAt]
  )

  // Aggregate token-level stats into network-level stats - memoized for performance
  // The aggregation itself is a pure function extracted to sacrifice-stats-aggregation.ts;
  // here we only memoize it and preserve the empty-array-when-no-data behavior.
  const networks: NetworkSacrificeStats[] = useMemo(
    () => (data ? aggregateNetworkStats(data, userAddresses) : []),
    [data, userAddresses]
  )

  // Create the grouped tokens map for easy access - memoized for performance
  const tokensByNetwork = useMemo(() => {
    const map = new Map<string, TokenSacrificeStats[]>()
    if (data) {
      // Create a Set of token keys that have valid price sources (sourceIsZero === false)
      const validTokenKeys = new Set(
        data.supportedTokens
          .filter(token => token.sourceIsZero === false)
          .map(token => keys.token(token))
      )

      // Filter steps to only include tokens with valid price sources
      const filteredSteps = data.steps.filter(token => validTokenKeys.has(keys.token(token)))

      // Initialize empty arrays for each network
      data.networks.forEach(network => {
        map.set(keys.network(network), [])
      })

      // Group tokens by their network ID (only supported tokens)
      filteredSteps.forEach(token => {
        const networkTokens = map.get(keys.network(token))
        if (networkTokens) {
          networkTokens.push(token)
        }
      })
    }
    return map
  }, [data])

  // Helper function to get tokens for a specific network
  const getTokensByNetworkId = useCallback(
    (networkId: keys.SerializedNetworkIdentifier): TokenSacrificeStats[] => {
      return tokensByNetwork.get(networkId) || []
    },
    [tokensByNetwork]
  )

  // Get supported tokens from stats
  const supportedTokens: SupportedTokenMetadataAndSearchable[] = useMemo(
    () => data?.supportedTokens || [],
    [data]
  )

  // Helper function to get supported tokens for a specific network
  const getSupportedTokensForNetwork = useCallback(
    (
      selectedNetworkId: keys.SerializedNetworkIdentifier
    ): SupportedTokenMetadataAndSearchable[] => {
      return supportedTokens.filter(token => keys.network(token) === selectedNetworkId)
    },
    [supportedTokens]
  )

  // Sum total sacrifices - memoized for performance
  const totalSacrifices = useMemo(() => {
    return networks.reduce((sum, network) => {
      return sum + BigInt(network.count || 0n)
    }, 0n)
  }, [networks])

  // Sum total value as BigInt for precision - memoized for performance
  const sacrificeTotalValue = useMemo(() => {
    return networks.reduce((sum, network) => {
      if (network.value === null || network.value === undefined) return sum
      try {
        return sum + BigInt(network.value)
      } catch {
        console.warn(`Invalid value for network ${keys.network(network)}: ${network.value}`)
        return sum
      }
    }, 0n)
  }, [networks])

  // Helper to find network by ID
  const networkFromId = useCallback(
    (id: keys.SerializedNetworkIdentifier) => {
      return networks.find(network => keys.network(network) === id) || null
    },
    [networks]
  )

  const userSacrifices = useMemo(() => {
    return data?.user?.sacrifices || null
  }, [data])

  const userStats = useMemo(() => {
    return data?.user?.stats || null
  }, [data])

  const userDestinationAddress = useMemo(() => {
    return data?.user?.pointsDestinationAddress || null
  }, [data])

  const userReferrerAddress = useMemo(() => {
    return data?.user?.referrerAddress || null
  }, [data])

  const value: SacrificeStatsContextValue = {
    data,
    isLoading: isPending,
    isFetching,
    isPlaceholderData,
    error: error as Error | null,
    lastUpdated,
    refetch: async () => {
      await refetch()
    },
    networks,
    totalSacrifices,
    totalPoints: BigInt(data?.totalPoints || 0),
    pointsVersion: data?.pointsVersion || null,
    sacrificeTotalValue,
    tokensByNetwork,
    supportedTokens,
    getTokensByNetworkId,
    getSupportedTokensForNetwork,
    networkFromId,
    userSacrifices,
    userStats,
    userDestinationAddress,
    userReferrerAddress,
    targetAddress: address,
    isQueryEnabled: queryEnabled,
  }

  return <SacrificeStatsContext.Provider value={value}>{children}</SacrificeStatsContext.Provider>
}
